import { useContext, useState } from "react"
import {
  Search,
  CheckCircle2,
  AlertCircle,
  PlusCircle,
  Trash2,
  Pencil,
  Check,
  X,
} from "lucide-react"
import { Button } from "@components/common/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@components/common/card"
import { Badge } from "@components/common/badge"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@components/common/table"
import { Input } from "@components/common/input"
import { Checkbox } from "@components/common/checkbox"
import { Alert, AlertDescription, AlertTitle } from "@components/common/alert"
import {
  BOQ_REQUEST_NEW_ITEM_DELETE,
  BOQ_REQUEST_NEW_ITEM_EDIT,
  FINANCIAL_SERVER_URL,
  REQUEST_FOR_APPROVING_BY_BOQ_ID_ENDPOINT,
} from "@pma/utils/api-routes"
import axios from "axios"
import { parseCookies } from "nookies"
import { ACCESS_TOKEN } from "@pma/utils/constants"
import { useMutation, useQuery } from "@tanstack/react-query"
import useTranslator from "@pma/hooks/use-translator"
import { useAuth } from "@pma/stores/auth-store"
import ApproveBoqItems from "./approve-boq-items"
import AddNewBoQItem from "./add-new-boq-item"
import { cn, getUserFromId } from "@pma/utils/helpers"
import { DictType } from "@pma/types"
import { Avatar, AvatarFallback, AvatarImage } from "@components/common/avatar"
import { Link } from "react-router-dom"
import { USERS_PAGE_ROUTE } from "@pma/utils/page-routes"
import PmsLoader from "@components/common/loader"
import { formatCurrency } from "@pma/utils/utils"
import { UserRoleContext } from "@pma/contexts/UserRoleContext"
import { toast } from "@pma/hooks/use-toast"

interface BoQDetailsProps {
  boqId: string
}

export function ApprovalBoQDetails({ boqId }: BoQDetailsProps) {
  const { authConfig } = useAuth()
  const { translate, locale } = useTranslator()

  const isHebrew = locale === "he"
  const accessToken = parseCookies()[ACCESS_TOKEN]
  const organizationUsers = authConfig?.organizationUsers

  const { userRoleDetails } = useContext(UserRoleContext)

  const [searchQuery, setSearchQuery] = useState("")
  const [selectedItems, setSelectedItems] = useState<string[]>([])
  const [showApproveDialog, setShowApproveDialog] = useState(false)
  const [showAddItemDialog, setShowAddItemDialog] = useState(false)
  const [editingItemId, setEditingItemId] = useState<string | null>(null)
  const [editFormData, setEditFormData] = useState<DictType>({})

  const getBoQForApproving = async () => {
    const response = await axios.post(
      FINANCIAL_SERVER_URL + REQUEST_FOR_APPROVING_BY_BOQ_ID_ENDPOINT,
      { boq_id: boqId },
      { headers: { Authorization: "Bearer " + accessToken } },
    )
    return response.data
  }

  const deleteBoqRequestedService = async (payload: any) => {
    const response = await axios.post(FINANCIAL_SERVER_URL + BOQ_REQUEST_NEW_ITEM_DELETE, payload, {
      headers: { Authorization: "Bearer " + accessToken },
    })

    return response.data
  }

  const editBoqRequestedService = async (payload: DictType) => {
    const response = await axios.post(FINANCIAL_SERVER_URL + BOQ_REQUEST_NEW_ITEM_EDIT, payload, {
      headers: { Authorization: "Bearer " + accessToken },
    })
    return response.data
  }

  const {
    data: forApprovingBoQDetails,
    isLoading: isLoadingApprovalBoQs,
    refetch: refetchMainBoQs,
  } = useQuery({
    queryKey: ["for-approving-boq", boqId],
    queryFn: getBoQForApproving,
  })

  const deleteSelectedItemsMutation = useMutation({
    mutationKey: ["delete-selected-new-items"],
    mutationFn: (payload: DictType) => deleteBoqRequestedService(payload),
    onSuccess: (data) => {
      toast({
        title: translate("Success"),
        description: translate("Requested items deleted"),
        variant: "success",
      })
      refetchMainBoQs()
      handleSelectAll(true)
    },
    onError: (error: DictType) => {
      const errorMessage = error?.response?.data?.message
      toast({
        title: translate("Error"),
        description: errorMessage || translate("Selected requested items not deleted"),
        variant: "error",
      })
    },
  })

  const editItemMutation = useMutation({
    mutationKey: ["edit-boq-new-item"],
    mutationFn: (payload: DictType) => editBoqRequestedService(payload),
    onSuccess: () => {
      toast({
        title: translate("Success"),
        description: translate("Item updated successfully"),
        variant: "success",
      })
      setEditingItemId(null)
      setEditFormData({})
      refetchMainBoQs()
    },
    onError: (error: DictType) => {
      const errorMessage = error?.response?.data?.message
      toast({
        title: translate("Error"),
        description: errorMessage || translate("Failed to update item"),
        variant: "error",
      })
    },
  })

  const handleEditClick = (item: DictType) => {
    setEditingItemId(item.id)
    setEditFormData({
      item_number: item?._boq_item?.number || item?.new_item_data?.number || "",
      item_description: item?._boq_item?.description || item?.new_item_data?.name || "",
      item_cost: item?._boq_item?.cost || item?.new_item_data?.cost || 0,
      item_amount: item?.requested_amount || 0,
      item_unit: item?._boq_item?.units || item?.new_item_data?.units || "",
      boq_new_item_id: item.id,
    })
  }

  const handleEditSubmit = () => {
    editItemMutation.mutate(editFormData)
  }

  const handleEditCancel = () => {
    setEditingItemId(null)
    setEditFormData({})
  }

  const boqItems: DictType[] = forApprovingBoQDetails?.items || []

  const filteredItems = boqItems.filter(
    (item) =>
      item?._boq_item?.number?.includes(searchQuery) ||
      item?._boq_item?.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item?.new_item_data?.number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item?.new_item_data?.name?.toLowerCase().includes(searchQuery.toLowerCase()),
  )

  const handleSelectItem = (itemId: string, reset?: boolean) => {
    if (reset) {
      setSelectedItems([])
    } else {
      setSelectedItems((prev) =>
        prev.includes(itemId) ? prev.filter((id) => id !== itemId) : [...prev, itemId],
      )
    }
  }

  const handleApproveSelected = () => setShowApproveDialog(true)

  const handleSelectAll = (reset?: boolean) => {
    if (selectedItems.length === filteredItems.length || reset) {
      setSelectedItems([])
    } else {
      const availableItems = filteredItems.filter((item) => {
        if (item?.new_item) {
          return item
        } else {
          return item?._boq_item?.is_item
        }
      })
      setSelectedItems(availableItems.map((item) => item?.id))
    }
  }

  const getStatusBadge = (item: DictType) => {
    if (item?.is_decline) {
      return (
        <Badge variant='disabled' className='flex items-center gap-1'>
          <span className='text-xs'>{translate("Declined")}</span>
        </Badge>
      )
    }
    if (item?.is_approved) {
      return (
        <Badge variant='default' className='flex items-center gap-1'>
          <CheckCircle2 className='h-3 w-3' /> {translate("Approved")}
        </Badge>
      )
    } else if (item?.is_signed) {
      return (
        <Badge variant='destructive' className='flex items-center gap-1'>
          <AlertCircle className='h-3 w-3' /> {translate("Signed")}
        </Badge>
      )
    } else {
      return (
        <Badge variant='secondary' className='flex items-center gap-1'>
          {translate("Pending")}
        </Badge>
      )
    }
  }

  return (
    <div className='space-y-6'>
      <Card>
        <CardHeader>
          <CardTitle>{translate("Approval BoQ Items")}</CardTitle>
          <CardDescription>
            {translate("View and approve (for approving) individual BoQ items")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className='space-y-4'>
            <div className='flex flex-col justify-between gap-4 md:flex-row'>
              <div className='flex flex-col gap-2 md:flex-row'>
                <div className='relative'>
                  <Search
                    className={cn(
                      "absolute top-2.5 h-4 w-4 text-muted-foreground",
                      isHebrew ? "right-2.5" : "left-2.5",
                    )}
                  />
                  <Input
                    placeholder={translate("Search items") + "..."}
                    className={cn("w-full md:w-[300px]", isHebrew ? "pr-8" : "pl-8")}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>

              <div className='flex flex-wrap gap-2'>
                {selectedItems.length > 0 && (
                  <Button
                    variant='outline'
                    size='sm'
                    className='border-red-200 text-red-700 hover:bg-red-100'
                    onClick={() => {
                      deleteSelectedItemsMutation.mutate({ boq_new_item_id: selectedItems })
                    }}
                    disabled={deleteSelectedItemsMutation.isPending}
                  >
                    <Trash2 className={cn("h-4 w-4", isHebrew ? "ml-1" : "mr-1")} />
                    {translate("Delete Items")}
                  </Button>
                )}
                {userRoleDetails?.supervisor || userRoleDetails?.fin_manager ? (
                  <Button
                    variant='default'
                    disabled={selectedItems.length === 0}
                    onClick={handleApproveSelected}
                    size={"sm"}
                  >
                    <CheckCircle2 className={cn("h-4 w-4", isHebrew ? "ml-1" : "mr-1")} />
                    {translate("Approve Selected")} ({selectedItems.length})
                  </Button>
                ) : null}

                <Button size={"sm"} variant='outline' onClick={() => setShowAddItemDialog(true)}>
                  <PlusCircle className={cn("h-4 w-4", isHebrew ? "ml-1" : "mr-1")} />
                  {translate("Add Item")}
                </Button>
              </div>
            </div>

            {selectedItems.length > 0 && (
              <Alert variant={"info"}>
                <CheckCircle2 className='h-4 w-4' />
                <AlertTitle>
                  {selectedItems.length} {translate("items selected")}
                </AlertTitle>
                <AlertDescription>
                  {translate("Use the buttons above to perform actions on the selected items")}.
                </AlertDescription>
              </Alert>
            )}

            <div className='rounded-md border'>
              <div className='border-b last:border-b-0'>
                <div className='overflow-x-auto'>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className='w-[50px]'>
                          <Checkbox
                            checked={
                              selectedItems.length > 0 &&
                              selectedItems.length === filteredItems.length
                            }
                            ref={(el: any) => {
                              if (el?.input) {
                                el.input.indeterminate =
                                  selectedItems.length > 0 &&
                                  selectedItems.length < filteredItems.length
                              }
                            }}
                            onCheckedChange={() => handleSelectAll()}
                            className='data-[state=indeterminate]:bg-primary data-[state=indeterminate]:text-primary-foreground'
                          />
                        </TableHead>
                        <TableHead className='w-[100px] cursor-pointer'>
                          <div className='flex items-center gap-1'>{translate("Number")}</div>
                        </TableHead>
                        <TableHead className='cursor-pointer'>
                          <div className='flex items-center gap-1'>{translate("Description")}</div>
                        </TableHead>
                        <TableHead
                          className={cn("w-[135px]", isHebrew ? "text-right" : "text-left")}
                        >
                          {translate("Status")}
                        </TableHead>
                        <TableHead className='w-[80px]'>{translate("Unit")}</TableHead>
                        <TableHead className='w-[120px] cursor-pointer text-right'>
                          <div className='flex items-center gap-1'>{translate("Requested By")}</div>
                        </TableHead>
                        <TableHead className='w-[160px] cursor-pointer text-right'>
                          <div className='flex items-center justify-end gap-1'>
                            {translate("Requested Amount")}
                          </div>
                        </TableHead>
                        <TableHead className='w-[100px] text-right'>{translate("Cost")}</TableHead>
                        <TableHead className='w-[100px]'>{translate("Actions")}</TableHead>
                      </TableRow>
                    </TableHeader>
                    {filteredItems?.length > 0 && !isLoadingApprovalBoQs && (
                      <TableBody>
                        {filteredItems?.map((item) => {
                          const isEditing = editingItemId === item.id
                          return (
                            <TableRow key={item.id}>
                              <TableCell className='text-left'>
                                <Checkbox
                                  checked={
                                    selectedItems.includes(item?._boq_item?.id) ||
                                    selectedItems.includes(item?.id)
                                  }
                                  onCheckedChange={() => handleSelectItem(item?.id)}
                                  disabled={isEditing}
                                />
                              </TableCell>
                              <TableCell className='font-mono text-sm'>
                                {isEditing ? (
                                  <Input
                                    value={editFormData.item_number}
                                    onChange={(e) =>
                                      setEditFormData((prev: DictType) => ({
                                        ...prev,
                                        item_number: e.target.value,
                                      }))
                                    }
                                    className='h-8 w-full'
                                  />
                                ) : (
                                  item?._boq_item?.number || item?.new_item_data?.number
                                )}
                              </TableCell>
                              <TableCell>
                                {isEditing ? (
                                  <Input
                                    value={editFormData.item_description}
                                    onChange={(e) =>
                                      setEditFormData((prev: DictType) => ({
                                        ...prev,
                                        item_description: e.target.value,
                                      }))
                                    }
                                    className='h-8 w-full'
                                  />
                                ) : (
                                  <div>
                                    {item?._boq_item?.description || item?.new_item_data?.name}
                                  </div>
                                )}
                              </TableCell>
                              <TableCell className={cn(isHebrew ? "text-right" : "text-left")}>
                                {getStatusBadge(item)}
                              </TableCell>
                              <TableCell>
                                {isEditing ? (
                                  <Input
                                    value={editFormData.item_unit}
                                    onChange={(e) =>
                                      setEditFormData((prev: DictType) => ({
                                        ...prev,
                                        item_unit: e.target.value,
                                      }))
                                    }
                                    className='h-8 w-full'
                                  />
                                ) : (
                                  item?._boq_item?.units || item?.new_item_data?.units || "---"
                                )}
                              </TableCell>
                              <TableCell className={cn(isHebrew ? "text-left" : "text-right")}>
                                <Link to={USERS_PAGE_ROUTE + `/${item?.designer_id}`}>
                                  <Button variant={"link"} size='sm' className='flex gap-2 p-0'>
                                    <Avatar className={cn("h-7 w-7", isHebrew ? "mr-1" : "ml-1")}>
                                      <AvatarImage
                                        src={
                                          getUserFromId(item?.requested_by_id, organizationUsers)
                                            .user_picture
                                            ? getUserFromId(
                                                item?.requested_by_id,
                                                organizationUsers,
                                              ).user_picture.url
                                            : `${process.env.PUBLIC_URL}/usr.svg`
                                        }
                                        alt='Avatar'
                                      />
                                      <AvatarFallback className='uppercase'>
                                        {getUserFromId(item?.requested_by_id, organizationUsers)
                                          ?.first_name &&
                                          getUserFromId(item?.requested_by_id, organizationUsers)
                                            ?.first_name[0]}
                                        {getUserFromId(item?.requested_by_id, organizationUsers)
                                          ?.last_name &&
                                          getUserFromId(item?.requested_by_id, organizationUsers)
                                            ?.last_name[0]}
                                      </AvatarFallback>
                                    </Avatar>{" "}
                                    {getUserFromId(item?.requested_by_id, organizationUsers)?.name}
                                  </Button>
                                </Link>
                              </TableCell>

                              <TableCell className='text-right'>
                                {isEditing ? (
                                  <Input
                                    type='number'
                                    value={editFormData.item_amount}
                                    onChange={(e) =>
                                      setEditFormData((prev: DictType) => ({
                                        ...prev,
                                        item_amount: parseFloat(e.target.value) || 0,
                                      }))
                                    }
                                    className='h-8 w-full'
                                  />
                                ) : (
                                  item?.requested_amount
                                )}
                              </TableCell>
                              <TableCell className='text-right'>
                                {isEditing ? (
                                  <Input
                                    type='number'
                                    value={editFormData.item_cost}
                                    onChange={(e) =>
                                      setEditFormData((prev: DictType) => ({
                                        ...prev,
                                        item_cost: parseFloat(e.target.value) || 0,
                                      }))
                                    }
                                    className='h-8 w-full'
                                  />
                                ) : (
                                  formatCurrency(
                                    item?._boq_item?.cost || item?.new_item_data?.cost,
                                    isHebrew,
                                  )
                                )}
                              </TableCell>

                              <TableCell>
                                {isEditing ? (
                                  <div className='flex items-center gap-1'>
                                    <Button
                                      onClick={handleEditSubmit}
                                      size={"sm"}
                                      variant='default'
                                      loading={editItemMutation.isPending}
                                      disabled={editItemMutation.isPending}
                                    >
                                      {!editItemMutation.isPending && <Check className='h-4 w-4' />}
                                    </Button>
                                    <Button
                                      onClick={handleEditCancel}
                                      size={"sm"}
                                      variant='outline'
                                      disabled={editItemMutation.isPending}
                                    >
                                      <X className='h-4 w-4' />
                                    </Button>
                                  </div>
                                ) : (
                                  <div className='flex items-center gap-1'>
                                    {userRoleDetails?.supervisor || userRoleDetails?.fin_manager ? (
                                      <Button
                                        onClick={() => {
                                          setSelectedItems([item.id])
                                          handleApproveSelected()
                                        }}
                                        size={"sm"}
                                        disabled={item?.is_approved}
                                      >
                                        {translate("Approve")}
                                      </Button>
                                    ) : null}
                                    <Button
                                      onClick={() => handleEditClick(item)}
                                      size={"sm"}
                                      variant='outline'
                                      disabled={editingItemId !== null}
                                    >
                                      <Pencil className='h-4 w-4' />
                                    </Button>
                                  </div>
                                )}
                              </TableCell>
                            </TableRow>
                          )
                        })}
                      </TableBody>
                    )}
                  </Table>
                  {isLoadingApprovalBoQs && <PmsLoader />}
                  {filteredItems?.length < 1 && !isLoadingApprovalBoQs && (
                    <div className='flex w-full items-center justify-center py-4 text-sm font-medium'>
                      <p>{translate("No items found")}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </CardContent>
        <CardFooter className='flex justify-between'>
          <div className='text-sm text-muted-foreground'>
            {translate("Showing")} {filteredItems?.length} {translate("of")} {boqItems?.length}{" "}
            {translate("items")}
          </div>
        </CardFooter>
      </Card>

      <ApproveBoqItems
        showApproveDialog={showApproveDialog}
        setShowApproveDialog={setShowApproveDialog}
        selectedItems={selectedItems}
        boqItems={boqItems}
        boqId={boqId}
        refetchBoQ={refetchMainBoQs}
        setSelectedItems={handleSelectItem}
        forApproval={true}
      />

      <AddNewBoQItem
        boqId={boqId}
        refetchBoQ={refetchMainBoQs}
        setShowAddItemDialog={setShowAddItemDialog}
        showAddItemDialog={showAddItemDialog}
      />
    </div>
  )
}
